import { formatCurrency } from "../../utilities";
import { CartPaymentSummaryModel, OrderOptionModel } from "../../models";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import { PaymentGatewayModel } from "../../models/PaymentGatewayModel";
import { GatewayEnum } from "../../enums";
import { DoorDashIcon } from "../../icons";
import Skeleton from "react-loading-skeleton";

type Props = {
  couponCode?: string;
  gateways: PaymentGatewayModel[];
  paymentBreakup: CartPaymentSummaryModel;
  orderOption: OrderOptionModel;
};

const CartPaymentSummary: React.FC<Props> = ({
  couponCode = "",
  gateways,
  paymentBreakup,
  orderOption,
}) => {
  const isCredit = gateways.some((g) =>
    [
      GatewayEnum.SQUAREUP,
      GatewayEnum.APPLEPAY,
      GatewayEnum.GOOGLEPAY,
      GatewayEnum.STRIPE,
    ].includes(g.gateway_id),
  );

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        <div className="fw-bold">Tax: {formatCurrency(paymentBreakup.tax)}</div>
        <div className="fw-bold mb-2">
          {isCredit ? "Credit Card Fee" : "Processing Fee"}:{" "}
          {formatCurrency(paymentBreakup.convenience)}
        </div>
        <div className="text-muted" style={{ fontSize: "13px" }}>
          * Thank you for supporting local! Ordering directly saves restaurants
          15-30% on third-party fees, and benefiting you with lower fees. This
          payment includes credit card processing fees paid to the restaurant.
        </div>
      </Popover.Body>
    </Popover>
  );

  if (Number(paymentBreakup.subtotal) <= 0) {
    return (
      <div className="d-flex flex-column gap-2">
        <Skeleton height={20} />
        <Skeleton height={20} />
        <Skeleton height={20} />
        <Skeleton height={20} />
      </div>
    );
  }

  return (
    <table>
      <tbody>
        <tr>
          <td>Subtotal</td>
          <td>{formatCurrency(paymentBreakup.subtotal)}</td>
        </tr>
        {(paymentBreakup.tax > 0 || paymentBreakup.convenience > 0) && (
          <tr>
            <td className="d-flex align-items-center gap-2">
              Taxes & Fees{" "}
              <OverlayTrigger placement="top" overlay={popover}>
                <InfoCircle className="cursor-pointer text-secondary" />
              </OverlayTrigger>
            </td>
            <td>
              {formatCurrency(
                Number(paymentBreakup.tax) + Number(paymentBreakup.convenience),
              )}
            </td>
          </tr>
        )}
        {paymentBreakup.shipping > 0 && (
          <tr>
            <td>
              Delivery{" "}
              {orderOption.third_party_delivery_order === "1" && (
                <DoorDashIcon size={18} />
              )}
            </td>
            <td>{formatCurrency(paymentBreakup.shipping)}</td>
          </tr>
        )}
        {/* {(paymentBreakup.convenience > 0) && (
          <tr>
            <td>Convenience Fee</td>
            <td>{formatCurrency(paymentBreakup.convenience)}</td>
          </tr>
        )} */}
        {paymentBreakup.discount > 0 && (
          <tr>
            <td className="highlight-text">
              Discount {couponCode && <>({couponCode})</>}
            </td>
            <td className="highlight-text">
              {formatCurrency(paymentBreakup.discount)}
            </td>
          </tr>
        )}
        {paymentBreakup.tip > 0 && (
          <tr>
            <td>Tip</td>
            <td>{formatCurrency(paymentBreakup.tip)}</td>
          </tr>
        )}
        {paymentBreakup.order_total > 0 && (
          <tr className="order-total-container">
            <td>
              <div className="order_price">Order Total</div>
            </td>
            <td>
              <div className="order_price">
                {formatCurrency(paymentBreakup.order_total)}
              </div>
            </td>
          </tr>
        )}
        {(paymentBreakup.loyalty || 0 > 0) && (
          <tr>
            <td className="highlight-text">Loyalty</td>
            <td className="highlight-text">
              {formatCurrency(paymentBreakup.loyalty)}
            </td>
          </tr>
        )}
        {paymentBreakup.giftcard > 0 && (
          <tr>
            <td className="highlight-text">Giftcard</td>
            <td className="highlight-text">
              {formatCurrency(paymentBreakup.giftcard)}
            </td>
          </tr>
        )}
        {/* {paymentBreakup.to_pay > 0 && (
          <tr className="order-total-container">
            <td>
              <div className="order_price">To Pay</div>
            </td>
            <td>
              <div className="order_price">
                {formatCurrency(paymentBreakup.to_pay)}
              </div>
            </td>
          </tr>
        )} */}
      </tbody>
    </table>
  );
};

export default CartPaymentSummary;
